import { Button } from "@progress/kendo-react-buttons";
import { Editor, EditorFocusEvent, EditorMountEvent, EditorTools, EditorUtils, NodeSpec, ProseMirror } from "@progress/kendo-react-editor";
import * as React from "react";
import * as sanitizeHtml from "sanitize-html";
import { placeholder, placeholderStyles } from "../../../../common/KendoEditorPlaceholderPlugin";
import { IConfig } from "../../../Config";

const { EditorState, EditorView, Schema } = ProseMirror;
const { imageResizing } = EditorUtils;

const InsertImageTool = (id) => (props) => {
    const { view } = props;
    const nodeType = view && view.state.schema.nodes.image;
    const canInsert = view && EditorUtils.canInsert(view.state, nodeType);
    return (
        <Button
            onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("openimgmanager", { detail: { id } }));
            }}
            disabled={!canInsert}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            icon="image"
            title="Image manager"
        />
    );
};

const InsertDocumentTool = (id) => (props) => {
    const { view } = props;
    return (
        <Button
            onClick={(e) => {
                e.preventDefault();
                if (view) {
                    const { from, to } = view.state.selection;
                    const selectedText = view.state.doc.textBetween(from, to);
                    window.dispatchEvent(new CustomEvent("opendocumentmanager", { detail: { id, selectedText } }));
                }
            }}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            icon="file"
            title="Document manager"
        />
    );
};

const createExpandEditorFullscreenTool = (onClick: (e: any) => void) => (
    (props) => (
        <Button
            onClick={onClick}
            icon="select-all"
            title="Maximize editor"
        />
    )
);

const htmlTagWhiteList = {
    allowedHtmlTags: "b, strong, i, em, u, del, s, sub, sup, ol, ul, li, p, span, h1, h2, h3, h4, h5, h6, a, img, iframe",
    allowedTagAttributes: { p: ["style"], a: ["href", "target", "title"], img: ["*"], iframe: ["*"] },
};

const iframe: NodeSpec = {
    // NodeSpec interface
    // http://prosemirror.net/docs/ref/#model.NodeSpec
    attrs: {
        src: { default: null },
        style: { default: null },
    },
    group: "block",
    parseDOM: [
        {
            getAttrs: (dom: HTMLElement) => ({
                src: dom.getAttribute("src"),
                style: dom.getAttribute("style"),
            }),
            tag: "iframe",
        },
    ],
    selectable: false,
    toDOM: (node) => {
        const attrs = {
            allowfullscreen: "true",
            frameborder: "0",
            src: node.attrs.src,
            style: node.attrs.style,
        };
        return ["iframe", attrs];
    },
};

interface IProps {
    config: IConfig;
    body: string;
    setBody: (value: string) => void;
    onFocus: (e: EditorFocusEvent) => void;
}

const {
    Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
    CleanFormatting, AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Indent, Outdent, OrderedList, UnorderedList, Undo, Redo, FormatBlock,
    Link, Unlink, ViewHtml,
} = EditorTools;

export default (props: IProps) => {
    const editor = React.useRef<Editor>(null);

    // const [filteredFeeds, setFilteredFeeds] = React.useState([]);
    const [isMaximized, setIsMaximized] = React.useState(false);

    React.useEffect(() => {
        const insertImg = (e: any) => {
            const view = editor.current?.view;
            if (!view || !e?.detail || e.detail.id !== props.config.portletInstanceId) {
                return;
            }
            const nodeType = view && view.state.schema.nodes.image;
            EditorUtils.insertNode(view, nodeType.createAndFill(e.detail));
        };
        window.addEventListener("insertimg", insertImg);
        const insertDoc = (e: any) => {
            const view = editor.current?.view;
            if (!view || !e?.detail || e.detail.id !== props.config.portletInstanceId) {
                return;
            }
            const { schema, tr } = view.state;
            const linkMarkType = schema.marks.link;
            const linkText = schema.text(e.detail.innerText, [linkMarkType.create({
                href: e.detail.href,
                id: e.detail.linkId,
                target: e.detail.target || "_self",
                title: e.detail.title,
            })]);
            if (tr.selection.from !== tr.selection.to) {
                const transaction = tr.replaceWith(tr.selection.from, tr.selection.to, linkText);
                view.dispatch(transaction);
            } else {
                const transaction = tr.insert(tr.selection.from, linkText);
                view.dispatch(transaction);
            }
        };
        window.addEventListener("insertdoc", insertDoc);
        return () => {
            window.removeEventListener("insertimg", insertImg);
            window.removeEventListener("insertdoc", insertDoc);
        };
    }, []);

    const handleSanitization = (text: string): string => {
        // Do not use Kendo Reacts' provided onPasteHtml sanitiser = pasteCleanup(sanitize(event.pastedHtml), pasteSettings)}... it's no good.
        const allowedHtmlTags = htmlTagWhiteList.allowedHtmlTags.split(",").map((t) => t.trim());
        const allowedHtmlAttributes = htmlTagWhiteList.allowedTagAttributes;

        const sanitizerSettings = {
            allowedAttributes: allowedHtmlAttributes,
            allowedTags: allowedHtmlTags,
        };

        const sanitizedText = sanitizeHtml(text, sanitizerSettings);
        return sanitizedText;
    };

    return (
        <div className={"text-editor " + (isMaximized ? "fullscreen-editor" : "")}>
            <Editor
                ref={(ref) => editor.current = ref}
                value={props.body || ""}
                onChange={(e) => {
                    props.setBody(e.html);
                }}
                contentStyle={{ height: isMaximized ? window.outerHeight : 200 }}
                tools={[
                    [Bold, Italic, Underline, Strikethrough],
                    [Subscript, Superscript],
                    [CleanFormatting],
                    [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                    [Indent, Outdent],
                    [OrderedList, UnorderedList],
                    FormatBlock,
                    [Undo, Redo],
                    [Link, Unlink, ViewHtml],
                    [
                        InsertImageTool(props.config.portletInstanceId),
                        InsertDocumentTool(props.config.portletInstanceId),
                        createExpandEditorFullscreenTool((e: any) => {
                            e.preventDefault();
                            setIsMaximized(!isMaximized);
                        }),
                    ],
                ]}
                onMount={(event: EditorMountEvent) => {
                    const { plugins, schema } = event.viewProps.state;
                    const nodes = schema.spec.nodes.addToEnd("iframe", iframe);
                    const marks = schema.spec.marks;
                    const customSchema = new Schema({ nodes, marks });
                    const doc = EditorUtils.createDocument(customSchema, "");

                    const editorDocument = event.dom.ownerDocument;
                    const styleElement = editorDocument && editorDocument.querySelector("style");
                    if (styleElement) {
                        styleElement.appendChild(editorDocument.createTextNode(placeholderStyles));
                    }

                    return new EditorView(
                        { mount: event.dom },
                        {
                            ...event.viewProps,
                            state: EditorState.create({
                                doc,
                                plugins: [
                                    ...plugins,
                                    imageResizing(),
                                    placeholder("Write your post..."),
                                ],
                            }),
                        },
                    );
                }}
                onPasteHtml={(event) => handleSanitization(event.pastedHtml)}
                onFocus={props.onFocus}
            />
        </div>
    );
};
